import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import IframeResizer from '@rio-cloud/iframe-resizer';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { NavLink } from 'react-router';

import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import { ConfigCatProvider, DataGovernance, PollingMode, createConsoleLogger } from 'configcat-react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { config } from '../../../config';
import { DEFAULT_LOCALE } from '../../../configuration/lang/lang';
import { getDisplayMessages, getLocale } from '../../../configuration/lang/langSlice';
import { getTenant, isUserSessionExpired } from '../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { getIdToken } from '../../../configuration/tokenHandling/tokenHandlingSlice';
import { MessageHandlerContainer } from '../containers/browsercommunication/MessageHandlerContainer';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from '../reducers/appSlice';
import {
    hasAnalyticsAnalysisOverlayParam,
    removeAnalyticsAnalysisOverlayParam,
} from '../utils/analyticsAnalysisOverlayUtils';
import { ReleaseNotesActionBarItem } from './ReleaseNotesActionBarItem';
import RouteUpdater from './RouteUpdater';
import { Assets } from './assets/Assets';
import EasyOnboardingDialog from './assets/easyOnboarding/EasyOnboardingDialog';
import { OrderChat } from '@rio-cloud/rio-order-chat-component';

const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

export const App = () => {
    const dispatch = useAppDispatch();
    const [showAnalyticsAnalysisOverlay] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return hasAnalyticsAnalysisOverlayParam();
    });

    const homeRoute = config.homeRoute as string;
    const isLocalhost = import.meta.env.DEV;
    const getLocalUrl = (port: number) => `http://${window.location.hostname}:${port}`;
    const resolveLink = (liveUrl: string | undefined, localPort: number) =>
        isLocalhost ? getLocalUrl(localPort) : (liveUrl ?? '');
    const languageData = useAppSelector(getDisplayMessages);
    const showSessionExpired = useAppSelector(
        state => isUserSessionExpired(state) && !getSessionExpiredAcknowledged(state)
    );
    const userLocale = useAppSelector(getLocale);
    const idToken = useAppSelector(getIdToken) ?? '';
    const getHeaderLinkTrackingAttributes = (label: string) =>
        getTrackingAttributes({
            trigger: 'click',
            category: 'link autoTracking',
            action: 'link click',
            label,
        });

    const navItems = [
        {
            key: 'assets',
            route: (
                <NavLink to='/assets' {...getHeaderLinkTrackingAttributes('application-header.assets')}>
                    <FormattedMessage id='intl-msg:sublink.assets' />
                </NavLink>
            ),
        },
        {
            key: 'drivers',
            route: (
                <a
                    href={resolveLink(config.external.driverAdministration, 3011)}
                    {...getHeaderLinkTrackingAttributes('application-header.drivers')}
                >
                    <FormattedMessage id='intl-msg:sublink.drivers' />
                </a>
            ),
        },
        {
            key: 'tags',
            route: (
                <a
                    href={resolveLink(config.external.tagAdministration, 3012)}
                    {...getHeaderLinkTrackingAttributes('application-header.tags')}
                >
                    <FormattedMessage id='intl-msg:sublink.tags' />
                </a>
            ),
        },
    ];

    const environment = import.meta.env.PROD ? 'production' : 'local';
    const tenant = useAppSelector(getTenant) || 'unknown';

    const appTitle = <FormattedMessage id={'intl-msg:moduleName'} />;
    const userMenu = (
        <DefaultUserMenu
            environment={environment}
            // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
            postLogout={{ redirectUri: config.login.postLogoutRedirectUri!, idTokenHint: idToken }}
        />
    );
    const menuUrl = config.backend.MENU_SERVICE as string;
    const chat = <OrderChat tenant={tenant} />;
    const notifications = <RioNotifications />;
    const releaseNotes = <ReleaseNotesActionBarItem />;
    const appNavigator = import.meta.env.VITE_PLAYWRIGHT_TEST_RUNTIME ? undefined : (
        <IframeResizer title='Application navigation' className='iFrameResizer' src={menuUrl} />
    );

    const homeLink = (
        <a
            href={isLocalhost ? window.location.origin : homeRoute}
            {...getHeaderLinkTrackingAttributes('application-header.home')}
        />
    );
    const logger = createConsoleLogger(config.configCat.logLevel); // Set the log level to INFO to track how your feature flags were evaluated. When moving to production, you can remove this line to avoid too detailed logging.

    useEffect(() => {
        if (!showAnalyticsAnalysisOverlay) {
            return;
        }

        removeAnalyticsAnalysisOverlayParam();
    }, [showAnalyticsAnalysisOverlay]);

    return (
        <IntlProvider
            defaultLocale={DEFAULT_LOCALE}
            key={userLocale}
            locale={userLocale ?? DEFAULT_LOCALE}
            // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
            messages={languageData!}
        >
            <ConfigCatProvider
                sdkKey={config.configCat.apiKey}
                pollingMode={PollingMode.LazyLoad}
                options={{
                    offline: !import.meta.env.PROD,
                    cacheTimeToLiveSeconds: config.configCat.pollingFrequencyInSeconds,
                    logger,
                    dataGovernance: DataGovernance.EuOnly,
                }}
            >
                <ApplicationLayout>
                    <ApplicationLayout.Header>
                        <ApplicationHeader
                            label={appTitle}
                            appNavigator={appNavigator}
                            homeRoute={homeLink}
                            navItems={navItems}
                            actionBarItems={[releaseNotes, chat, notifications, userMenu]}
                        />
                        {/*Do not include EULA popup here, so that user is able to manage their fleet*/}
                        {/*<EulaConsent environment={environment} />*/}
                    </ApplicationLayout.Header>
                    <NotificationsContainer />
                    <SessionExpiredDialog
                        locale={userLocale ?? DEFAULT_LOCALE}
                        onClose={() => dispatch(hideSessionExpiredDialog())}
                        show={showSessionExpired}
                    />
                    <EasyOnboardingDialog />
                    <MessageHandlerContainer />
                    <RouteUpdater />
                    <Assets />
                    {showAnalyticsAnalysisOverlay && (
                        <Suspense fallback={null}>
                            <AnalyticsAnalysisOverlay
                                bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                            />
                        </Suspense>
                    )}
                </ApplicationLayout>
            </ConfigCatProvider>
        </IntlProvider>
    );
};
